import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { InlineSpinnerComponent } from '@shared-components/inline-spinner/inline-spinner.component';

type ButtonVariant =
  | 'flat'
  | 'stroked'
  | 'raised'
  | 'basic'
  | 'icon'
  | 'mini-fab';

type IconPosition = 'left' | 'right';

@Component({
  selector: 'gd-button',
  templateUrl: './bo-button.component.html',
  styleUrls: ['./bo-button.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltip,
    InlineSpinnerComponent,
  ],
})
export class BoButtonComponent implements OnChanges {
  @Input() label: string | null = null;
  @Input() iconName: string | null = null;
  @Input() iconPosition: IconPosition = 'right';
  @Input() iconFontSet: string = 'material-icons';

  @Input() colorType: string = 'primary';
  @Input() disabled: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() classes: string = '';
  @Input() buttonType: string = 'button';
  /**
   * HTML `form` attribute: lets a `buttonType="submit"` button placed outside
   * the <form> (e.g. in mat-dialog-actions) submit it. Never combine a submit
   * button with `(buttonClick)`: the form's submit handler is the only path.
   */
  @Input() formId: string | null = null;
  @Input() variant: ButtonVariant = 'flat';
  @Input() tooltip: string = '';
  /** Accessible name when the visible label lacks context (rows, icons). */
  @Input() ariaLabel: string | null = null;

  @Output() buttonClick: EventEmitter<MouseEvent> = new EventEmitter();

  /**
   * Width of the button right before it starts loading, used as `min-width`
   * so swapping the icon/label for the spinner does not make it jump.
   */
  lockedMinWidth: number | null = null;

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    const loading = changes['isLoading'];
    if (loading && loading.currentValue && !loading.previousValue) {
      // ngOnChanges runs before the view updates: the DOM still has the idle width.
      const button = this.host.nativeElement.querySelector('button');
      this.lockedMinWidth = button
        ? button.getBoundingClientRect().width
        : null;
    }
  }

  onClickButton(event: MouseEvent): void {
    if (this.disabled || this.isLoading) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.buttonClick.emit(event);
  }

  get hostClasses(): string[] {
    return [
      this.classes,
      this.colorType === 'secondary' ? 'secondary-button' : '',
    ].filter(Boolean);
  }

  get isIconOnlyVariant(): boolean {
    return this.variant === 'icon' || this.variant === 'mini-fab';
  }
}
